import React, { createContext, useMemo, useContext } from 'react';

import { CITY, COMPANY, LINE1, POSTAL_CODE } from '../../../../address/constants';
import { TAX_IDENTIFIER_FIELD } from '../constants/tax-identifier';
import { useTaxForm } from '../hooks/use-tax-form';
import { useTaxIdConfig } from '../hooks/use-tax-id-config';

/** @typedef {import('../types/tax-types').TaxData} TaxData */
/** @typedef {import('../types/tax-types').InvoiceAddress} InvoiceAddress */
/** @typedef {import('../types/tax-types').UseTaxFormType} UseTaxFormType */
/** @typedef {(field: string, value: string) => void} FormHandlerType */

const withInvoiceAddressRequiredFields = [COMPANY, LINE1, CITY, POSTAL_CODE, TAX_IDENTIFIER_FIELD];

export const TaxFormContext = createContext({});

/**
 * @returns {object | UseTaxFormType}
 */
export const useTaxFormContext = () => useContext(TaxFormContext);

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.isEditing]
 * @param {string} [props.source]
 * @returns {React.JSX.Element}
 */
export const TaxFormContextProvider = ({ children, isEditing: isEditingInitialValue = true, source = '' }) => {
  const { isInvoiceAddressRequired } = useTaxIdConfig();
  const requiredFields = isInvoiceAddressRequired ? withInvoiceAddressRequiredFields : [TAX_IDENTIFIER_FIELD];

  const {
    invoiceAddress,
    setInvoiceAddressField,
    taxIdentifier,
    setTaxIdentifier,
    issuingAuthority,
    setIssuingAuthorityValue,
    fieldsWithError,
    validateFormField,
    submitTaxForm,
    isInvalidForm,
    isEditing,
    closeProcess,
    isTaxSectionOpen,
    setIsTaxSectionOpen,
  } = useTaxForm({ requiredFields, isEditing: isEditingInitialValue, source });

  const value = useMemo(
    () => ({
      invoiceAddress,
      setInvoiceAddressField,
      taxIdentifier,
      setTaxIdentifier,
      issuingAuthority,
      setIssuingAuthorityValue,
      fieldsWithError,
      validateFormField,
      submitTaxForm,
      isInvalidForm,
      isEditing,
      closeProcess,
      isTaxSectionOpen,
      setIsTaxSectionOpen,
    }),
    [
      invoiceAddress,
      setInvoiceAddressField,
      taxIdentifier,
      setTaxIdentifier,
      issuingAuthority,
      setIssuingAuthorityValue,
      fieldsWithError,
      validateFormField,
      submitTaxForm,
      isInvalidForm,
      isEditing,
      closeProcess,
      isTaxSectionOpen,
      setIsTaxSectionOpen,
    ],
  );

  return <TaxFormContext.Provider value={value}>{children}</TaxFormContext.Provider>;
};
